import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MESSAGE_READ_STATUS,
  MESSAGE_READ_STATUS_ID,
  SEARCH_TYPES
} from 'features/message-center/constants';
import { useChatContext } from 'features/message-center/providers';
import { getConversationsV2, searchV2, updateReadStatus } from 'features/message-center/services';
import {
  findExtremeItemsByDate,
  getDraftAttachment,
  getDraftMessage,
  sortConversationsByDate,
  updateConversationList
} from 'features/message-center/utils';
import { useShowAlert } from 'hooks';
import strings from 'strings';
import { UIStatus } from 'UI/constants/status';
import { getParamFromUrl, removeParamsFromUrl } from 'UI/utils';

import { useNotificationHandler } from './useNotificationHandler';

const CONVERSATIONS_LIMIT = 20;
const URL_PARAMS = {
  NewConversationPhoneNumber: 'newConversationPhoneNumber',
  Conversation: 'conversation'
};

const getUpdatedConversations = ({ newReadStatus, selectedConversations, conversations }) => {
  const { ids: selectedIdsSet, externalNumbers: selectedExternalNumbers } =
    selectedConversations.reduce(
      (acc, { id, externalNumber }) => {
        acc.ids.add(id);
        acc.externalNumbers.push(externalNumber);
        return acc;
      },
      { ids: new Set(), externalNumbers: [] }
    );

  return {
    updatedConversations: conversations.map(conversation =>
      selectedIdsSet.has(conversation.id)
        ? { ...conversation, unreadMessages: newReadStatus }
        : conversation
    ),
    selectedExternalNumbers
  };
};

export const useTextConversations = () => {
  const newConversationPhoneNumber = getParamFromUrl(URL_PARAMS.NewConversationPhoneNumber);
  const { showAlert } = useShowAlert();
  const { internalNumber } = useChatContext();
  const hasMoreItems = useRef(false);
  const [conversations, setConversations] = useState([]);
  const [searchResults, setSearchResults] = useState({
    contacts: [],
    conversations: []
  });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedConversations, setSelectedConversations] = useState([]);
  const [filterParams, setFilterParams] = useState({
    readStatus: MESSAGE_READ_STATUS.all
  });
  const [uiState, setUiState] = useState({
    error: null,
    isSearchContactsDrawerOpen: !!newConversationPhoneNumber,
    isSearching: false,
    status: UIStatus.Default,
    newConversationPhoneNumber,
    openTemplates: false,
    isQuickViewOpen: false,
    showSelectConversations: false
  });

  const fetchConversations = useCallback(
    async ({ searchKeyword = '' } = {}) => {
      if (!internalNumber) return;

      try {
        setUiState(prev => ({ ...prev, status: UIStatus.Loading }));
        setConversations([]);

        const fetchFunction = searchKeyword ? searchV2 : getConversationsV2;
        const params = {
          ...(searchKeyword ? { readStatus: MESSAGE_READ_STATUS.all } : filterParams),
          limit: CONVERSATIONS_LIMIT,
          internalNumber,
          ...(searchKeyword && { search: searchKeyword })
        };

        const response = await fetchFunction(params);
        hasMoreItems.current = response?.hasMoreItems ?? false;

        if (searchKeyword) {
          setSearchResults({
            contacts:
              response?.conversations?.map(item => ({
                ...item,
                draftMessage: getDraftMessage(item.id),
                draftAttachment: getDraftAttachment(item.id),
                searchType: SEARCH_TYPES.contact
              })) ?? [],
            conversations:
              response?.messages?.map(item => ({
                ...item,
                searchType: SEARCH_TYPES.message
              })) ?? []
          });
        } else {
          const updatedConversations = (response?.conversations ?? []).map(conversation => ({
            ...conversation,
            draftMessage: getDraftMessage(conversation.id),
            draftAttachment: getDraftAttachment(conversation.id)
          }));
          setConversations(updatedConversations);
        }
        setUiState(prev => ({ ...prev, status: UIStatus.Success, error: null }));
      } catch (error) {
        setUiState(prev => ({ ...prev, status: UIStatus.Error, error }));

        showAlert({
          title: strings.shared.errors.serverError.title,
          error
        });
      }
    },
    [filterParams, internalNumber, showAlert]
  );

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    const encodedConversation = getParamFromUrl(URL_PARAMS.Conversation);
    if (!encodedConversation) return;

    try {
      const decodedConversation = atob(encodedConversation);
      const conversationEntity = JSON.parse(decodedConversation);
      setSelectedConversation(conversationEntity);

      removeParamsFromUrl();
    } catch (error) {
      /* ignore error */
    }
  }, []);

  const handleUpdateConversation = useCallback(({ id, updates }) => {
    if (!id) return;

    setConversations(prev =>
      sortConversationsByDate(
        updateConversationList({
          conversations: prev,
          id,
          updates
        })
      )
    );
  }, []);

  const handleNewNotification = useCallback(
    incomingMessage => {
      const id = incomingMessage?.conversationId;

      if (!id || filterParams.readStatus === MESSAGE_READ_STATUS.read) return;

      const unreadMessages =
        selectedConversation?.id === incomingMessage.conversationId
          ? 0
          : incomingMessage.unreadMessages;

      const isNewConversation = !conversations.some(conversation => conversation.id === id);
      const { conversationId: _conversationId, ...incomingMessageWithoutConversationId } =
        incomingMessage;

      if (isNewConversation) {
        setConversations(prev =>
          sortConversationsByDate([
            ...prev,
            {
              ...incomingMessageWithoutConversationId,
              id,
              unreadMessages
            }
          ])
        );
        return;
      }

      handleUpdateConversation({
        id,
        updates: {
          ...incomingMessageWithoutConversationId,
          id,
          unreadMessages
        }
      });
    },
    [selectedConversation, conversations, filterParams.readStatus, handleUpdateConversation]
  );

  useNotificationHandler({
    onNewNotification: handleNewNotification
  });

  const handleClickConversation = conversation => {
    setSelectedConversation(conversation);
    setUiState(prev => ({ ...prev, error: null }));
  };

  const handleSearch = (searchKeyword = '') => {
    setUiState(prev => ({ ...prev, isSearching: !!searchKeyword }));
    setSearchResults({
      contacts: [],
      conversations: []
    });

    fetchConversations({ searchKeyword });
  };

  const handleRefresh = async externalNumber => {
    if (uiState.status === UIStatus.Loading) return;

    hasMoreItems.current = false;
    setConversations([]);
    setSearchResults({
      contacts: [],
      conversations: []
    });
    setUiState(prev => ({ ...prev, isSearching: false, showSelectConversations: false }));
    await fetchConversations();

    const updatedSelectedConversation = conversations?.find(
      conversation => conversation.externalNumber === externalNumber
    );
    setSelectedConversation(updatedSelectedConversation || null);
  };

  const handleFetchMoreConversations = async () => {
    if (!hasMoreItems.current || uiState.status === UIStatus.Loading) return;

    try {
      setUiState(prev => ({ ...prev, status: UIStatus.Loading }));

      const { oldest: oldestConversation } = findExtremeItemsByDate(conversations);
      if (!oldestConversation) return;

      const newParams = {
        limit: CONVERSATIONS_LIMIT,
        internalNumber,
        lastItemDate: oldestConversation.date,
        lastItemExternalNumber: oldestConversation.externalNumber
      };

      const response = await getConversationsV2(newParams);
      hasMoreItems.current = response?.hasMoreItems ?? false;
      setConversations(prev => [...prev, ...(response?.conversations ?? [])]);
      setUiState(prev => ({ ...prev, status: UIStatus.Success }));
    } catch (e) {
      setUiState(prev => ({ ...prev, status: UIStatus.Error }));
    }
  };

  const handleCloseSearchContactsDrawer = () => {
    setUiState(prev => ({
      ...prev,
      isSearchContactsDrawerOpen: false,
      newConversationPhoneNumber: null
    }));
    removeParamsFromUrl();
  };

  const handleOpenSearchContactsDrawer = () => {
    setUiState(prev => ({
      ...prev,
      isSearchContactsDrawerOpen: true,
      showSelectConversations: false
    }));
  };

  const handleMessageChange = (payload = {}) => {
    const updatedConversations = conversations.map(conversation => {
      if (conversation.id === selectedConversation.id) {
        return { ...conversation, ...payload };
      }
      return conversation;
    });

    setConversations(updatedConversations);
  };

  const handleMarkConversations = async newStatus => {
    const previousConversations = [...conversations];

    const clearSelection = () => {
      toggleSelectConversations(false);
      setSelectedConversations([]);
    };

    try {
      const newReadStatus =
        newStatus === MESSAGE_READ_STATUS.unread
          ? MESSAGE_READ_STATUS_ID.unread
          : MESSAGE_READ_STATUS_ID.read;

      const { updatedConversations, selectedExternalNumbers } = getUpdatedConversations({
        newReadStatus,
        selectedConversations,
        conversations
      });

      const hasChanges = updatedConversations.some(
        (conv, i) => conv.unreadMessages !== conversations[i].unreadMessages
      );

      if (!hasChanges) {
        clearSelection();
        return;
      }

      const isAllFilter = filterParams.readStatus === MESSAGE_READ_STATUS.all;

      if (isAllFilter) {
        setConversations(updatedConversations);
        clearSelection();
      }

      await updateReadStatus({
        internalNumber,
        externalNumbers: selectedExternalNumbers,
        status: newReadStatus
      });

      if (!isAllFilter) {
        clearSelection();
        await handleRefresh();
      }
    } catch (error) {
      setConversations(previousConversations);
      showAlert({
        title: strings.shared.errors.serverError.title,
        error
      });
    }
  };

  const handleClickConversationCheckbox = useCallback(
    ({ id, isSelected }) => {
      const conversation = conversations.find(conv => conv.id === id);
      if (!conversation) return;

      setSelectedConversations(prev => {
        const alreadySelected = prev.some(sel => sel.id === id);
        if (isSelected) {
          if (alreadySelected) return prev;
          return [...prev, conversation];
        }

        if (!alreadySelected) return prev;

        return prev.filter(sel => sel.id !== id);
      });
    },
    [conversations]
  );

  const handleChangeFilterParams = (key, value) => {
    setFilterParams(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const toggleSelectConversations = value => {
    setSelectedConversations([]);
    setUiState(prev => ({
      ...prev,
      showSelectConversations: value ?? !prev.showSelectConversations
    }));
  };
  const toggleTemplates = () => {
    toggleSelectConversations(false);
    setUiState(prev => ({ ...prev, openTemplates: !prev.openTemplates }));
  };
  const toggleQuickViewOpen = () => {
    toggleSelectConversations(false);
    setUiState(prev => ({ ...prev, isQuickViewOpen: !prev.isQuickViewOpen }));
  };

  const finalConversations = uiState.isSearching ? { ...searchResults } : conversations;

  return {
    conversations: finalConversations,
    filterParams,
    handleChangeFilterParams,
    handleClickConversation,
    handleClickConversationCheckbox,
    handleCloseSearchContactsDrawer,
    handleFetchMoreConversations,
    handleMarkConversations,
    handleMessageChange,
    handleNewNotification,
    handleOpenSearchContactsDrawer,
    handleRefresh,
    handleSearch,
    handleUpdateConversation,
    selectedConversation,
    selectedConversations,
    toggleQuickViewOpen,
    toggleSelectConversations,
    toggleTemplates,
    uiState
  };
};
